import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import contactService from '../../services/contactService';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await contactService.sendMessage(formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      toast.error(error.message || 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <h1 className="contact-hero-title">Get in Touch</h1>
          <p className="contact-hero-subtitle">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-form-section">
        <div className="container">
          <div className="contact-content">
            <div className="form-container">
              <h2>Send Us a Message</h2>
              <p className="form-description">Fill out the form below and we'll get back to you within 24 hours</p>

              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      autoComplete="name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      autoComplete="email"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject *</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="How can we help you?"
                    autoComplete="off"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us more about your inquiry..."
                    rows="6"
                    required
                  ></textarea>
                </div>

                <button type="submit" className="btn btn-theme-blue btn-submit" disabled={submitting}>
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>

            <div className="contact-sidebar">
              <div className="sidebar-card">
                <h3>
                  <span className="material-symbols-outlined sidebar-icon">schedule</span>
                  Business Hours
                </h3>
                <div className="hours-list">
                  <div className="hours-item">
                    <span>Monday - Friday</span>
                    <span>9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="hours-item">
                    <span>Saturday</span>
                    <span>10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="hours-item">
                    <span>Sunday</span>
                    <span>Closed</span>
                  </div>
                </div>
              </div>

              <div className="sidebar-card">
                <h3>
                  <span className="material-symbols-outlined sidebar-icon">mail</span>
                  Contact Us
                </h3>
                <a href="mailto:contact@hiringopen.com" className="contact-email">
                  contact@hiringopen.com
                </a>
                <h3>
                  <span className="material-symbols-outlined sidebar-icon">share</span>
                  Follow Us
                </h3>
                <p className="social-description">Stay updated with latest job opportunities and company news</p>
                <div className="social-links-horizontal">
                  <a href="https://www.facebook.com/HiringOpenInc/" target="_blank" rel="noopener noreferrer" className="social-link facebook" title="Follow us on Facebook">
                    <i className="fab fa-facebook-f"></i>
                  </a>
                  <a href="https://www.linkedin.com/company/hiringopen/" target="_blank" rel="noopener noreferrer" className="social-link linkedin" title="Follow us on LinkedIn">
                    <i className="fab fa-linkedin-in"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      {/* <section className="contact-map">
        <div className="container">
          <h2 className="section-title">Our Location</h2>
          <div className="map-placeholder">
            <div className="map-content">
              <span className="material-symbols-outlined map-icon">map</span>
              <p>123 Business Street, Tech City, TC 12345</p>
            </div>
          </div>
        </div>
      </section> */}

      {/* FAQ Section */}
      <section className="contact-faq">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-vertical-container">
            <div className="faq-card-vertical">
              <div className="faq-icon"><span className="material-symbols-outlined">help</span></div>
              <div className="faq-content">
                <h3 className="faq-question">How quickly will I get a response?</h3>
                <p className="faq-answer">We typically respond to all inquiries within 24 hours during business days.</p>
              </div>
            </div>
            <div className="faq-card-vertical">
              <div className="faq-icon"><span className="material-symbols-outlined">call</span></div>
              <div className="faq-content">
                <h3 className="faq-question">Can I schedule a call?</h3>
                <p className="faq-answer">Yes! Mention your preferred time in the message and we'll arrange a call.</p>
              </div>
            </div>
            <div className="faq-card-vertical">
              <div className="faq-icon"><span className="material-symbols-outlined">build</span></div>
              <div className="faq-content">
                <h3 className="faq-question">Do you offer technical support?</h3>
                <p className="faq-answer">Absolutely! Our technical support team is available via email and live chat.</p>
              </div>
            </div>
            <div className="faq-card-vertical">
              <div className="faq-icon"><span className="material-symbols-outlined">bug_report</span></div>
              <div className="faq-content">
                <h3 className="faq-question">Where can I report a bug?</h3>
                <p className="faq-answer">Please use the contact form above and select "Technical Issue" as the subject.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
